/**
 * Categorías — CRUD con jerarquía simple (parentId).
 *   - Tabla con name, slug, level, productos count, status
 *   - Modal crear/editar con name, slug, parent select, image, position, isActive
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Input, Label, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { Plus, FolderTree, Edit, Trash2, X } from 'lucide-react'

export const Route = createFileRoute('/store/$id/categories')({
  component: CategoriesPage,
})

interface Category {
  id: string
  slug: string
  parentId: string | null
  level: number
  image: string | null
  position: number
  isActive: boolean
  name: string | null
  description: string | null
  productCount: number
  createdAt: string
}

function CategoriesPage() {
  const { activeTenantId } = useAuth()
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; cat?: Category } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => tenantApi.get<{ data: Category[] }>('/api/v1/categories'),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []

  return (
    <>
      <PageHeader
        title="Categorías"
        description="Secciones, subsecciones y categorías del catálogo"
        action={
          <Button size="sm" onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4 mr-2" /> Nueva categoría
          </Button>
        }
      />

      <div className="p-8 space-y-4">
        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <FolderTree className="size-12 text-muted-foreground mb-3" />
            <p className="text-base font-medium">Sin categorías</p>
            <p className="text-sm text-muted-foreground mt-1">Crea la primera categoría para organizar tu catálogo.</p>
          </Card>
        )}
        {list.length > 0 && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Nombre</th>
                    <th className="text-left px-4 py-3">Slug</th>
                    <th className="text-center px-4 py-3">Nivel</th>
                    <th className="text-right px-4 py-3">Productos</th>
                    <th className="text-center px-4 py-3">Estado</th>
                    <th className="text-right px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.map((c) => (
                    <CatRow key={c.id} cat={c} categories={list} onEdit={() => setDialog({ mode: 'edit', cat: c })} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {dialog && <CategoryDialog mode={dialog.mode} cat={dialog.cat} categories={list} onClose={() => setDialog(null)} />}
    </>
  )
}

function CatRow({ cat, categories, onEdit }: { cat: Category; categories: Category[]; onEdit: () => void }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => tenantApi.del(`/api/v1/categories/${cat.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  })
  const parent = categories.find((p) => p.id === cat.parentId)
  return (
    <tr className="hover:bg-[hsl(var(--surface))]">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {cat.level > 0 && <span className="text-muted-foreground">↳</span>}
          {cat.image
            ? <img src={cat.image} className="size-8 rounded object-cover" />
            : <div className="size-8 rounded bg-[hsl(var(--muted))] flex items-center justify-center"><FolderTree className="size-3.5 text-muted-foreground" /></div>}
          <div>
            <p className="font-medium">{cat.name ?? cat.slug}</p>
            {parent && <p className="text-xs text-muted-foreground">en {parent.name ?? parent.slug}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">/{cat.slug}</td>
      <td className="px-4 py-3 text-center text-muted-foreground">{cat.level}</td>
      <td className="px-4 py-3 text-right tabular-nums">{cat.productCount}</td>
      <td className="px-4 py-3 text-center">
        <Badge variant={cat.isActive ? 'success' : 'muted'}>{cat.isActive ? 'Activa' : 'Inactiva'}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onEdit} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Editar">
            <Edit className="size-4" />
          </button>
          <button
            onClick={() => { if (confirm('¿Eliminar esta categoría?')) remove.mutate() }}
            className="p-1.5 rounded text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40"
            title="Eliminar"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

function CategoryDialog({ mode, cat, categories, onClose }: { mode: 'create' | 'edit'; cat?: Category; categories: Category[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(cat?.name ?? '')
  const [slug, setSlug] = useState(cat?.slug ?? '')
  const [parentId, setParentId] = useState<string>(cat?.parentId ?? '')
  const [image, setImage] = useState(cat?.image ?? '')
  const [position, setPosition] = useState(cat?.position ?? 0)
  const [isActive, setIsActive] = useState(cat?.isActive ?? true)

  const save = useMutation({
    mutationFn: (payload: any) =>
      mode === 'create'
        ? tenantApi.post('/api/v1/categories', payload)
        : tenantApi.patch(`/api/v1/categories/${cat!.id}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] })
      onClose()
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    save.mutate({
      name,
      slug,
      parentId: parentId || null,
      image: image || null,
      position,
      isActive,
    })
  }

  // Solo categorías nivel 0 pueden ser padres; excluir self al editar
  const possibleParents = categories.filter((c) => c.level === 0 && c.id !== cat?.id)

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Nueva categoría' : 'Editar categoría'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="size-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label>Nombre</Label>
            <Input value={name} onChange={(e) => {
              setName(e.target.value)
              if (mode === 'create' && !slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
            }} required autoFocus />
          </div>
          <div>
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} required />
          </div>
          <div>
            <Label>Categoría padre (opcional)</Label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full h-9 px-3 bg-[hsl(var(--surface))] border border-border rounded-md text-sm"
            >
              <option value="">— Sin padre (categoría raíz) —</option>
              {possibleParents.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? p.slug}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Imagen URL (opcional)</Label>
              <Input type="url" value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label>Posición</Label>
              <Input type="number" min="0" value={position} onChange={(e) => setPosition(parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="ca" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label htmlFor="ca" className="text-sm">Activa</label>
          </div>
          {save.error && (
            <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Guardando...' : (mode === 'create' ? 'Crear' : 'Guardar')}</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
